import type { FeatureEntryInput } from './types'

export const FEATURE_REGISTRY = {
  // ---------------------------------------------------------------------------
  // Placement modes
  // ---------------------------------------------------------------------------
  'placement-text': {
    glossaryKey: 'placement-mode',
    category: 'placement',
    complexity: 'beginner',
    prereqs: [],
    shortcut: 'T',
    sourceFiles: ['src/lib/engine/hooks/useEngineKeyboard.ts'],
    testSpecs: ['keyboard/placement-modes.spec.ts'],
    tip: 'Press T to place a text annotation on the canvas.',
  },
  'placement-image': {
    glossaryKey: 'placement-mode',
    category: 'placement',
    complexity: 'beginner',
    prereqs: [],
    shortcut: 'I',
    sourceFiles: ['src/lib/engine/hooks/useEngineKeyboard.ts'],
    testSpecs: ['keyboard/placement-modes.spec.ts'],
  },
  'placement-video': {
    glossaryKey: 'placement-mode',
    category: 'placement',
    complexity: 'beginner',
    prereqs: [],
    shortcut: 'V',
    sourceFiles: ['src/lib/engine/hooks/useEngineKeyboard.ts'],
    testSpecs: ['keyboard/placement-modes.spec.ts'],
    tip: 'Press V to create a new shot on the canvas.',
  },
  'placement-audio': {
    glossaryKey: 'placement-mode',
    category: 'placement',
    complexity: 'beginner',
    prereqs: [],
    shortcut: 'A',
    sourceFiles: ['src/lib/engine/hooks/useEngineKeyboard.ts'],
    testSpecs: ['keyboard/placement-modes.spec.ts'],
  },
  'placement-bin': {
    glossaryKey: 'bin',
    category: 'placement',
    complexity: 'beginner',
    prereqs: [],
    shortcut: 'B',
    sourceFiles: ['src/lib/engine/hooks/useEngineKeyboard.ts'],
    testSpecs: ['keyboard/placement-modes.spec.ts'],
    tip: 'Press B to create a bin for related shots.',
  },
  'placement-set': {
    glossaryKey: 'set',
    category: 'placement',
    complexity: 'intermediate',
    prereqs: [],
    shortcut: 'Shift+S',
    sourceFiles: ['src/lib/engine/hooks/useEngineKeyboard.ts'],
    testSpecs: ['keyboard/placement-modes.spec.ts'],
  },
  'placement-variable': {
    glossaryKey: 'variable',
    category: 'placement',
    complexity: 'intermediate',
    prereqs: [],
    shortcut: 'Shift+V',
    sourceFiles: ['src/lib/engine/hooks/useEngineKeyboard.ts'],
    testSpecs: ['keyboard/placement-modes.spec.ts'],
    tip: 'Press Shift+V to create a variable for reusable prompt fragments.',
  },

  // ---------------------------------------------------------------------------
  // Selection
  // ---------------------------------------------------------------------------
  'selection-select-all': {
    glossaryKey: 'canvas',
    category: 'selection',
    complexity: 'beginner',
    prereqs: [],
    shortcut: 'Cmd+A',
    sourceFiles: ['src/lib/engine/hooks/useEngineKeyboard.ts'],
    testSpecs: ['keyboard/selection.spec.ts', 'keyboard/multi-select-operations.spec.ts'],
  },
  'selection-escape': {
    glossaryKey: 'canvas',
    category: 'selection',
    complexity: 'beginner',
    prereqs: [],
    shortcut: 'Escape',
    sourceFiles: ['src/lib/engine/hooks/useEngineKeyboard.ts'],
    testSpecs: ['keyboard/selection.spec.ts'],
  },
  'selection-delete': {
    glossaryKey: 'node',
    category: 'delete',
    complexity: 'beginner',
    prereqs: ['selection-click'],
    shortcut: 'Delete',
    sourceFiles: [
      'src/lib/engine/hooks/useEngineKeyboard.ts',
      'src/components/canvas/shared/hooks/useCanvasKeyboardShortcuts.ts',
    ],
    testSpecs: ['keyboard/selection.spec.ts', 'keyboard/multi-select-operations.spec.ts'],
  },
  'selection-click': {
    glossaryKey: 'node',
    category: 'selection',
    complexity: 'beginner',
    prereqs: [],
    shortcut: null,
    sourceFiles: ['src/lib/engine/CanvasEngine.ts'],
    testSpecs: ['mouse/click-select.spec.ts'],
  },
  'selection-shift-click': {
    glossaryKey: 'node',
    category: 'selection',
    complexity: 'beginner',
    prereqs: ['selection-click'],
    shortcut: null,
    sourceFiles: ['src/lib/engine/CanvasEngine.ts'],
    testSpecs: ['mouse/click-select.spec.ts'],
  },
  'selection-click-empty': {
    glossaryKey: 'canvas',
    category: 'selection',
    complexity: 'beginner',
    prereqs: ['selection-click'],
    shortcut: null,
    sourceFiles: ['src/lib/engine/CanvasEngine.ts'],
    testSpecs: ['mouse/click-select.spec.ts'],
  },
  'selection-marquee': {
    glossaryKey: 'marquee',
    category: 'selection',
    complexity: 'intermediate',
    prereqs: ['selection-click'],
    shortcut: null,
    sourceFiles: ['src/lib/engine/CanvasEngine.ts'],
    testSpecs: ['mouse/marquee.spec.ts'],
    tip: 'Click and drag on empty canvas to draw a selection rectangle.',
  },
  'selection-hover': {
    glossaryKey: 'node',
    category: 'selection',
    complexity: 'beginner',
    prereqs: [],
    shortcut: null,
    sourceFiles: ['src/lib/engine/CanvasEngine.ts'],
    testSpecs: ['mouse/hover.spec.ts'],
  },

  // ---------------------------------------------------------------------------
  // Rating & color labels
  // ---------------------------------------------------------------------------
  'rating-reject': {
    glossaryKey: 'rating',
    category: 'rating',
    complexity: 'beginner',
    prereqs: ['selection-click'],
    shortcut: 'R',
    sourceFiles: ['src/components/canvas/shared/hooks/useCanvasKeyboard.ts'],
    testSpecs: ['keyboard/rating-flags.spec.ts', 'keyboard/multi-select-operations.spec.ts'],
  },
  'rating-favorite': {
    glossaryKey: 'rating',
    category: 'rating',
    complexity: 'beginner',
    prereqs: ['selection-click'],
    shortcut: 'F',
    sourceFiles: ['src/components/canvas/shared/hooks/useCanvasKeyboard.ts'],
    testSpecs: ['keyboard/rating-flags.spec.ts'],
  },
  'rating-upscale': {
    glossaryKey: 'shot',
    category: 'rating',
    complexity: 'intermediate',
    prereqs: ['selection-click'],
    shortcut: 'U',
    sourceFiles: ['src/components/canvas/shared/hooks/useCanvasKeyboard.ts'],
    testSpecs: ['keyboard/rating-flags.spec.ts'],
  },
  'rating-star-score': {
    glossaryKey: 'rating',
    category: 'rating',
    complexity: 'beginner',
    prereqs: ['selection-click'],
    shortcut: '1-5',
    sourceFiles: ['src/lib/engine/hooks/useEngineKeyboard.ts'],
    testSpecs: ['keyboard/rating-flags.spec.ts'],
    tip: 'Select a shot and press 1-5 to apply a star rating.',
  },
  'rating-color-labels': {
    glossaryKey: 'color-label',
    category: 'rating',
    complexity: 'beginner',
    prereqs: ['selection-click'],
    shortcut: '6-0',
    sourceFiles: ['src/lib/engine/hooks/useEngineKeyboard.ts'],
    testSpecs: ['keyboard/rating-flags.spec.ts', 'keyboard/color-label-batch.spec.ts'],
    tip: 'Select a shot and press 6-0 to apply a color label for visual organization.',
  },
  'rating-filter-overlay': {
    glossaryKey: 'rating',
    category: 'rating',
    complexity: 'intermediate',
    prereqs: ['rating-reject', 'rating-favorite'],
    shortcut: 'Shift+F',
    sourceFiles: ['src/components/canvas/shared/hooks/useCanvasKeyboard.ts'],
    testSpecs: ['keyboard/rating-flags.spec.ts'],
  },

  // ---------------------------------------------------------------------------
  // Camera / viewport
  // ---------------------------------------------------------------------------
  'camera-fit-all': {
    glossaryKey: 'viewport',
    category: 'camera',
    complexity: 'beginner',
    prereqs: [],
    shortcut: 'Shift+1',
    sourceFiles: ['src/lib/engine/hooks/useV3CameraShortcuts.ts'],
    testSpecs: ['keyboard/camera-shortcuts.spec.ts'],
    tip: 'Press Shift+1 to fit all nodes into view.',
  },
  'camera-zoom-to-selection': {
    glossaryKey: 'viewport',
    category: 'camera',
    complexity: 'intermediate',
    prereqs: ['selection-click'],
    shortcut: 'Shift+2 / Shift+Z',
    sourceFiles: ['src/lib/engine/hooks/useV3CameraShortcuts.ts'],
    testSpecs: ['keyboard/camera-shortcuts.spec.ts'],
  },
  'camera-center-selection': {
    glossaryKey: 'viewport',
    category: 'camera',
    complexity: 'beginner',
    prereqs: ['selection-click'],
    shortcut: '.',
    sourceFiles: ['src/lib/engine/hooks/useV3CameraShortcuts.ts'],
    testSpecs: ['keyboard/camera-shortcuts.spec.ts'],
  },
  'camera-zoom-in': {
    glossaryKey: 'viewport',
    category: 'camera',
    complexity: 'beginner',
    prereqs: [],
    shortcut: '+',
    sourceFiles: ['src/components/canvas/shared/hooks/useCanvasKeyboard.ts'],
    testSpecs: ['keyboard/zoom-pan.spec.ts'],
  },
  'camera-zoom-out': {
    glossaryKey: 'viewport',
    category: 'camera',
    complexity: 'beginner',
    prereqs: [],
    shortcut: '-',
    sourceFiles: ['src/components/canvas/shared/hooks/useCanvasKeyboard.ts'],
    testSpecs: ['keyboard/zoom-pan.spec.ts'],
  },
  'camera-viewport-reset': {
    glossaryKey: 'viewport',
    category: 'camera',
    complexity: 'beginner',
    prereqs: [],
    shortcut: 'Shift+0',
    sourceFiles: ['src/lib/engine/hooks/useV3CameraShortcuts.ts'],
    testSpecs: ['keyboard/camera-shortcuts.spec.ts', 'keyboard/zoom-pan.spec.ts'],
  },
  'camera-hand-tool': {
    glossaryKey: 'viewport',
    category: 'camera',
    complexity: 'beginner',
    prereqs: [],
    shortcut: 'H',
    sourceFiles: ['src/lib/engine/hooks/useEngineHandMode.ts'],
    testSpecs: [],
    tip: 'Press H for the hand tool and drag to pan the canvas. Press Escape to return to select.',
  },
  'camera-pan-hold': {
    glossaryKey: 'viewport',
    category: 'camera',
    complexity: 'beginner',
    prereqs: [],
    shortcut: 'Shift+Space',
    sourceFiles: ['src/lib/engine/hooks/useEngineHandMode.ts'],
    testSpecs: [],
    tip: 'Hold Shift+Space and drag to pan. Space alone plays or pauses.',
  },

  // ---------------------------------------------------------------------------
  // Clipboard
  // ---------------------------------------------------------------------------
  'clipboard-copy': {
    glossaryKey: 'node',
    category: 'clipboard',
    complexity: 'beginner',
    prereqs: ['selection-click'],
    shortcut: 'Cmd+C',
    sourceFiles: ['src/lib/engine/hooks/useEngineClipboard.ts'],
    testSpecs: ['keyboard/clipboard.spec.ts', 'keyboard/clipboard-extended.spec.ts'],
  },
  'clipboard-cut': {
    glossaryKey: 'node',
    category: 'clipboard',
    complexity: 'beginner',
    prereqs: ['selection-click'],
    shortcut: 'Cmd+X',
    sourceFiles: ['src/lib/engine/hooks/useEngineClipboard.ts'],
    testSpecs: ['keyboard/clipboard.spec.ts', 'keyboard/clipboard-extended.spec.ts'],
  },
  'clipboard-paste': {
    glossaryKey: 'node',
    category: 'clipboard',
    complexity: 'beginner',
    prereqs: ['clipboard-copy'],
    shortcut: 'Cmd+V',
    sourceFiles: ['src/lib/engine/hooks/useEngineClipboard.ts'],
    testSpecs: ['keyboard/clipboard.spec.ts', 'keyboard/clipboard-extended.spec.ts'],
  },

  // ---------------------------------------------------------------------------
  // Duplicate
  // ---------------------------------------------------------------------------
  'duplicate-selection': {
    glossaryKey: 'node',
    category: 'duplicate',
    complexity: 'beginner',
    prereqs: ['selection-click'],
    shortcut: 'Cmd+D',
    sourceFiles: ['src/lib/engine/hooks/useEngineKeyboard.ts'],
    testSpecs: ['keyboard/duplicate.spec.ts', 'keyboard/multi-select-operations.spec.ts'],
    tip: 'Select nodes and press Cmd+D to duplicate them in place.',
  },
  'duplicate-reuse-settings': {
    glossaryKey: 'shot',
    category: 'duplicate',
    complexity: 'advanced',
    prereqs: ['duplicate-selection'],
    shortcut: 'Cmd+Shift+D',
    sourceFiles: ['src/lib/engine/hooks/useEngineKeyboard.ts'],
    testSpecs: ['keyboard/duplicate.spec.ts'],
  },

  // ---------------------------------------------------------------------------
  // Playback
  // ---------------------------------------------------------------------------
  'playback-toggle': {
    glossaryKey: 'shot',
    category: 'playback',
    complexity: 'beginner',
    prereqs: ['selection-click'],
    shortcut: 'Space',
    sourceFiles: ['src/lib/engine/hooks/useEnginePlaybackKeyboard.ts'],
    testSpecs: ['keyboard/playback.spec.ts'],
    tip: 'Select a shot and press Space to play or pause it.',
  },
  'playback-scrub': {
    glossaryKey: 'shot',
    category: 'playback',
    complexity: 'intermediate',
    prereqs: ['playback-toggle'],
    shortcut: 'J/K/L',
    sourceFiles: ['src/lib/engine/hooks/useEnginePlaybackKeyboard.ts'],
    testSpecs: ['keyboard/playback.spec.ts'],
    tip: 'Use J/K/L to scrub through a shot, the same as in professional editors.',
  },
  'playback-seek-bounds': {
    glossaryKey: 'shot',
    category: 'playback',
    complexity: 'intermediate',
    prereqs: ['playback-toggle'],
    shortcut: 'Home/End',
    sourceFiles: ['src/lib/engine/hooks/useEnginePlaybackKeyboard.ts'],
    testSpecs: ['keyboard/playback.spec.ts'],
  },
  'playback-frame-step': {
    glossaryKey: 'shot',
    category: 'playback',
    complexity: 'intermediate',
    prereqs: ['playback-toggle'],
    shortcut: 'ArrowLeft/ArrowRight',
    sourceFiles: ['src/lib/engine/hooks/useEnginePlaybackKeyboard.ts'],
    testSpecs: ['keyboard/playback.spec.ts'],
  },

  // ---------------------------------------------------------------------------
  // Undo / Redo
  // ---------------------------------------------------------------------------
  'undo-redo': {
    glossaryKey: 'canvas',
    category: 'undo-redo',
    complexity: 'beginner',
    prereqs: [],
    shortcut: 'Cmd+Z / Cmd+Shift+Z',
    sourceFiles: ['src/lib/undoManager.ts'],
    testSpecs: ['keyboard/undo-redo.spec.ts'],
  },

  // ---------------------------------------------------------------------------
  // Drag
  // ---------------------------------------------------------------------------
  'drag-node': {
    glossaryKey: 'node',
    category: 'drag',
    complexity: 'beginner',
    prereqs: ['selection-click'],
    shortcut: null,
    sourceFiles: ['src/lib/engine/DragManager.ts'],
    testSpecs: ['mouse/drag-node.spec.ts'],
  },
  'drag-bin-asset': {
    glossaryKey: 'bin',
    category: 'drag',
    complexity: 'intermediate',
    prereqs: ['drag-node', 'placement-bin'],
    shortcut: null,
    sourceFiles: ['src/lib/engine/DragManager.ts'],
    testSpecs: ['mouse/bin-drag.spec.ts'],
  },
  'drag-bin-extended': {
    glossaryKey: 'bin',
    category: 'drag',
    complexity: 'intermediate',
    prereqs: ['drag-bin-asset'],
    shortcut: null,
    sourceFiles: ['src/lib/engine/DragManager.ts'],
    testSpecs: ['mouse/bin-drag-extended.spec.ts'],
  },
  'drag-stack': {
    glossaryKey: 'stack',
    category: 'drag',
    complexity: 'intermediate',
    prereqs: ['drag-node'],
    shortcut: null,
    sourceFiles: ['src/lib/engine/DragManager.ts'],
    testSpecs: ['mouse/stack-drag.spec.ts'],
  },
  'drag-stack-drop': {
    glossaryKey: 'stack',
    category: 'drag',
    complexity: 'advanced',
    prereqs: ['drag-stack'],
    shortcut: null,
    sourceFiles: ['src/lib/engine/DragManager.ts'],
    testSpecs: ['mouse/collapsed-stack-drop.spec.ts'],
  },

  // ---------------------------------------------------------------------------
  // Context menu
  // ---------------------------------------------------------------------------
  'context-menu-node': {
    glossaryKey: 'context-menu',
    category: 'context-menu',
    complexity: 'beginner',
    prereqs: [],
    shortcut: null,
    sourceFiles: ['src/components/canvas/CanvasPanelV3/contextMenu.ts'],
    testSpecs: ['mouse/context-menu.spec.ts'],
  },
  'context-menu-variable': {
    glossaryKey: 'variable',
    category: 'context-menu',
    complexity: 'intermediate',
    prereqs: ['context-menu-node'],
    shortcut: null,
    sourceFiles: ['src/components/canvas/CanvasPanelV3/contextMenu.ts'],
    testSpecs: ['mouse/variable-context-menu.spec.ts', 'mouse/variable-context-menu-actions.spec.ts'],
  },
  'context-menu-stack': {
    glossaryKey: 'stack',
    category: 'context-menu',
    complexity: 'intermediate',
    prereqs: ['context-menu-node'],
    shortcut: null,
    sourceFiles: [
      'src/components/canvas/CanvasPanelV3/contextMenu.ts',
      'src/components/canvas/CanvasPanelV3/components/StackContextMenu.tsx',
    ],
    testSpecs: ['mouse/stack-context-menu.spec.ts', 'mouse/stack-context-menu-actions.spec.ts'],
  },

  // ---------------------------------------------------------------------------
  // Double-click
  // ---------------------------------------------------------------------------
  'double-click-detail': {
    glossaryKey: 'shot',
    category: 'selection',
    complexity: 'beginner',
    prereqs: ['selection-click'],
    shortcut: null,
    sourceFiles: ['src/lib/engine/hooks/useEngineDoubleClick.ts'],
    testSpecs: ['mouse/double-click.spec.ts'],
  },
  'double-click-annotation-edit': {
    glossaryKey: 'annotation',
    category: 'annotation',
    complexity: 'intermediate',
    prereqs: ['selection-click'],
    shortcut: null,
    sourceFiles: ['src/lib/engine/hooks/useEngineDoubleClick.ts'],
    testSpecs: ['mouse/double-click.spec.ts'],
  },

  // ---------------------------------------------------------------------------
  // Wheel / trackpad
  // ---------------------------------------------------------------------------
  'wheel-zoom': {
    glossaryKey: 'viewport',
    category: 'wheel',
    complexity: 'beginner',
    prereqs: [],
    shortcut: null,
    sourceFiles: ['src/lib/engine/CameraController.ts'],
    testSpecs: ['wheel/zoom-wheel.spec.ts'],
  },
  'wheel-pinch': {
    glossaryKey: 'viewport',
    category: 'wheel',
    complexity: 'beginner',
    prereqs: [],
    shortcut: null,
    sourceFiles: ['src/lib/engine/CameraController.ts'],
    testSpecs: ['wheel/zoom-wheel.spec.ts'],
  },
  'wheel-pan': {
    glossaryKey: 'viewport',
    category: 'wheel',
    complexity: 'beginner',
    prereqs: [],
    shortcut: null,
    sourceFiles: ['src/lib/engine/CameraController.ts'],
    testSpecs: ['wheel/zoom-wheel.spec.ts'],
  },

  // ---------------------------------------------------------------------------
  // Drag-drop
  // ---------------------------------------------------------------------------
  'drop-file': {
    glossaryKey: 'canvas',
    category: 'drop',
    complexity: 'beginner',
    prereqs: [],
    shortcut: null,
    sourceFiles: ['src/lib/engine/CanvasEngine.ts'],
    testSpecs: ['drag-drop/file-drop.spec.ts'],
  },

  // ---------------------------------------------------------------------------
  // Stack lifecycle
  // ---------------------------------------------------------------------------
  'stack-lifecycle': {
    glossaryKey: 'stack',
    category: 'stack',
    complexity: 'intermediate',
    prereqs: ['selection-click'],
    shortcut: 'Alt+ArrowLeft/Alt+ArrowRight',
    sourceFiles: ['src/lib/engine/hooks/useEngineKeyboard.ts'],
    testSpecs: ['keyboard/stack-lifecycle.spec.ts', 'mouse/stack-drag.spec.ts'],
    tip: 'Use Alt + arrow keys to cycle through stacked shots and compare takes.',
  },

  // ---------------------------------------------------------------------------
  // Variable shortcuts
  // ---------------------------------------------------------------------------
  'variable-shortcuts': {
    glossaryKey: 'variable',
    category: 'variable',
    complexity: 'intermediate',
    prereqs: ['placement-variable'],
    shortcut: null,
    sourceFiles: ['src/lib/engine/hooks/useEngineKeyboard.ts'],
    testSpecs: ['keyboard/variable-shortcuts.spec.ts'],
  },

  // ---------------------------------------------------------------------------
  // Inline rename
  // ---------------------------------------------------------------------------
  'inline-rename': {
    glossaryKey: 'inline-rename',
    category: 'rename',
    complexity: 'intermediate',
    prereqs: ['selection-click'],
    shortcut: 'Enter/Escape',
    sourceFiles: ['src/lib/engine/hooks/useEngineKeyboard.ts'],
    testSpecs: ['keyboard/inline-rename.spec.ts'],
    tip: 'Press Enter on a selected node to rename it directly on the canvas.',
  },

  // ---------------------------------------------------------------------------
  // Set viewer
  // ---------------------------------------------------------------------------
  'set-viewer-navigation': {
    glossaryKey: 'set-viewer',
    category: 'set-viewer',
    complexity: 'advanced',
    prereqs: [],
    shortcut: 'WASD/QE/Space',
    sourceFiles: ['src/components/set/SetViewerKeyboardShortcuts.tsx'],
    testSpecs: ['set-viewer/navigation.spec.ts'],
  },
} as const satisfies Record<string, FeatureEntryInput>

export type FeatureId = keyof typeof FEATURE_REGISTRY

export function getFeature(id: FeatureId): FeatureEntryInput & { id: FeatureId } {
  return { id, ...FEATURE_REGISTRY[id] }
}

export function getFeaturesByCategory(category: FeatureEntryInput['category']) {
  return (Object.keys(FEATURE_REGISTRY) as FeatureId[])
    .filter(id => FEATURE_REGISTRY[id].category === category)
    .map(id => getFeature(id))
}

export function getFeaturesByComplexity(complexity: FeatureEntryInput['complexity']) {
  return (Object.keys(FEATURE_REGISTRY) as FeatureId[])
    .filter(id => FEATURE_REGISTRY[id].complexity === complexity)
    .map(id => getFeature(id))
}
